/**
 * Who else tends this garden, on the profile.
 *
 * Invite by email; they get a link and land in the same garden. Members
 * see and change the same plants, photos and notes. What stays theirs:
 * their profile, how the app looks to them, their billing, and anything
 * they grow in a garden of their own.
 *
 * Removing somebody, or leaving a garden, asks first: it used to happen at
 * the touch of a small ×, and sending the invitation again is the only way
 * back from it.
 *
 * An invitation nobody took up in time says "Invitation expired" and offers
 * Send again, which invites the same address again (a fresh link, the days
 * counted from now). Inviting somebody who has already joined is refused,
 * and said so under the box.
 *
 * Used by: UserProfile (?s=people). The same list (GARDEN_PEOPLE_KEY) feeds
 * the header's garden switcher and the tips.
 *
 * Surprising: "Tend" and leaving the garden being tended reload the page
 * (shared/api/gardenMembers chooseGarden), because no cached query says
 * which garden it is about. The chosen garden rides on every request as the
 * X-Grow-Garden header.
 *
 * A read, an invitation or a removal that fails on our side (a 5xx or no
 * answer) says the standard sentence, reported to Sentry, with the support
 * address as a link and no "Try again" (D31); refusals keep their own words.
 *
 * @module GardenPeopleCard
 */

import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import Spinner from '@/shared/components/ui/Spinner';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { chooseGarden, chosenGarden, getGardenPeople, inviteToGarden, removeGardenMember } from '@/shared/api/gardenMembers';
import { ProfileScreen } from './ProfileScreen';
import { SettingList } from './profileKit';

import { GARDEN_PEOPLE_KEY } from './profileQueries';
import { errorMessage } from '@/shared/lib/errorMessage';
import { isServerFailure, rateLimitedText } from '@/shared/lib/serverFailure';
import { FailureText, ServerFailureNote } from '@/shared/components/ServerFailure';

/** Who is about to be removed, or which garden left, while the question is open. */
type Pending = { memberId: number; name: string; leaving: boolean; ownerId?: number } | null;

/** Profile > Garden people: who tends this garden, the invite box, and the gardens this person tends. */
export const GardenPeopleScreen: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { reactTranslate: t } = useTranslation();
  const queryClient = useQueryClient();
  // A read that failed is said, rather than "Loading" for ever.
  const { data, isLoading, isError, error: loadError } = useQuery({ queryKey: GARDEN_PEOPLE_KEY, queryFn: getGardenPeople, enabled: open });
  const [email, setEmail] = React.useState('');
  const [said, setSaid] = React.useState<{ ok: boolean; text: string } | null>(null);
  const [pending, setPending] = React.useState<Pending>(null);
  const inputId = React.useId();
  const here = chosenGarden();

  React.useEffect(() => { if (open) setSaid(null); }, [open]);

  /**
   * Sends an invitation, from the box or from an expired one's Send again,
   * and says under the box whether the email went. The box is only emptied
   * when it held the address just invited, so sending one again from the
   * list does not wipe what is being typed.
   */
  const invite = useMutation({
    mutationFn: (address: string) => inviteToGarden(address),
    onSuccess: (result) => {
      setEmail((current) => (current.trim().toLowerCase() === result.email ? '' : current));
      queryClient.invalidateQueries({ queryKey: GARDEN_PEOPLE_KEY });
      setSaid({ ok: true, text: result.sent ? t('profile.people.invited', { email: result.email }) : t('profile.people.invitedNoEmail', { email: result.email }) });
    },
    // A garden with as many invitations waiting as it may have says so,
    // rather than the general failure: withdrawing one is the way on. So
    // does inviting somebody who is already in.
    onError: (err, address) => {
      const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setSaid({
        ok: false,
        text: code === 'TOO_MANY_INVITES'
          ? t('profile.people.tooManyInvites')
          : code === 'ALREADY_MEMBER'
            ? t('profile.people.alreadyMember', { email: address })
            : errorMessage(err, t('profile.people.inviteFailed')),
      });
    },
  });
  /** Ends a membership: somebody shown out of this garden, or this person leaving another's. */
  const remove = useMutation({
    mutationFn: (memberId: number) => removeGardenMember(memberId),
    onSuccess: () => { setPending(null); queryClient.invalidateQueries({ queryKey: GARDEN_PEOPLE_KEY }); },
    onError: (err) => { setPending(null); setSaid({ ok: false, text: errorMessage(err, t('profile.people.removeFailed')) }); },
  });

  return (
    <ProfileScreen open={open} title={t('profile.people.title')} onClose={onClose}>
      <p className="text-sm text-muted-foreground">{t('profile.people.intro')}</p>
      {!data && isError ? (
        isServerFailure(loadError)
          ? <ServerFailureNote error={loadError} where="GardenPeopleCard" />
          : <p role="alert" className="text-sm text-destructive"><FailureText text={rateLimitedText(loadError) ?? t('sitewide.loadFailed')} /></p>
      ) : isLoading || !data ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner size={16} />{t('sitewide.loading')}</div>
      ) : (
        <>
          {data.members.length > 0 && (
            <SettingList>
              {data.members.map((member) => (
                <div key={member.memberId} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-[0.9375rem] font-medium">{member.name || member.email}</p>
                    <p className="truncate text-sm text-muted-foreground">
                      {member.accepted
                        ? member.email
                        : member.expired ? t('profile.people.expired') : t('profile.people.pending')}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    {!member.accepted && member.expired && (
                      <Button
                        variant="outline"
                        size="sm"
                        busy="sending"
                        loading={invite.isPending && invite.variables === member.email}
                        disabled={invite.isPending}
                        onClick={() => { setSaid(null); invite.mutate(member.email); }}
                      >
                        {t('profile.people.sendAgain')}
                      </Button>
                    )}
                    <Button
                      variant={!member.accepted && member.expired ? 'ghost' : 'outline'}
                      size="sm"
                      onClick={() => setPending({ memberId: member.memberId, name: member.name || member.email, leaving: false })}
                    >
                      {t('profile.people.removeShort')}
                    </Button>
                  </div>
                </div>
              ))}
            </SettingList>
          )}

          <form
            className="space-y-1.5"
            onSubmit={(event) => { event.preventDefault(); if (email.trim()) invite.mutate(email.trim()); }}
          >
            <Label htmlFor={inputId}>{t('profile.people.inviteLabel')}</Label>
            <div className="flex gap-2">
              <Input
                id={inputId}
                type="email"
                value={email}
                onChange={(event) => { setEmail(event.target.value); setSaid(null); }}
                placeholder={t('profile.people.invitePlaceholder')}
                className="flex-1"
              />
              <Button type="submit" busy="sending" loading={invite.isPending} disabled={email.trim() === ''}>
                <UserPlus className="h-4 w-4" aria-hidden="true" />
                <span className="ml-2">{t('profile.people.invite')}</span>
              </Button>
            </div>
            {/* Said here, under the box it answers, not in a toast. */}
            {said && (
              <p aria-live="polite" className={said.ok ? 'text-sm text-primary' : 'text-sm text-destructive'}>
                <FailureText text={said.text} />
              </p>
            )}
          </form>

          {/* Gardens somebody else has let this person into. Leaving one is
              the member's own to do, which is why it sits here and not only
              in the owner's list. */}
          {data.gardens.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">{t('profile.people.theirGardens')}</p>
              <SettingList>
                {data.gardens.map((garden) => (
                  <div key={garden.memberId} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate text-[0.9375rem] font-medium">{t('profile.people.gardenOf', { name: garden.ownerName })}</p>
                      {here === garden.ownerId && <p className="text-sm text-muted-foreground">{t('profile.people.tendingNow')}</p>}
                    </div>
                    <div className="flex shrink-0 gap-1">
                      {here !== garden.ownerId && (
                        <Button variant="outline" size="sm" onClick={() => chooseGarden(garden.ownerId)}>
                          {t('profile.people.tend')}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPending({ memberId: garden.memberId, name: garden.ownerName, leaving: true, ownerId: garden.ownerId })}
                      >
                        {t('profile.people.leave')}
                      </Button>
                    </div>
                  </div>
                ))}
              </SettingList>
            </div>
          )}
        </>
      )}

      <BottomPanel
        open={pending !== null}
        onOpenChange={(next) => { if (!next && !remove.isPending) setPending(null); }}
        title={pending?.leaving
          ? t('profile.people.leaveTitle', { name: pending?.name ?? '' })
          : t('profile.people.removeTitle', { name: pending?.name ?? '' })}
      >
        <p className="text-sm text-muted-foreground">
          {pending?.leaving ? t('profile.people.leaveBody') : t('profile.people.removeBody')}
        </p>
        <ActionRow cancelLabel={t('profile.people.keep')} disabled={remove.isPending} onCancel={() => setPending(null)}>
          <Button
            variant="destructive"
            busy="deleting"
            loading={remove.isPending}
            onClick={() => {
              if (!pending) return;
              // Leaving the garden being tended switches back to their own,
              // which reloads the page: only once the leaving is done, or the
              // reload would cut the request off and they would still be a member.
              const backToOwn = pending.leaving && here === pending.ownerId;
              remove.mutate(pending.memberId, backToOwn ? { onSuccess: () => chooseGarden(null) } : undefined);
            }}
          >
            {pending?.leaving ? t('profile.people.leave') : t('profile.people.removeShort')}
          </Button>
        </ActionRow>
      </BottomPanel>
    </ProfileScreen>
  );
};
